/**
 * AccountContextCenter Component
 * 
 * Unified account and context switching component that appears in the sidebar footer.
 * - Desktop: Popover
 * - Mobile: Bottom Sheet
 * 
 * Shows:
 * - Firm name and user email
 * - Platform badge (Super Admin) and business role badge
 * - Business switcher (hidden in single business mode)
 * - Role-aware quick actions
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building,
  CreditCard,
  Puzzle,
  HelpCircle,
  LogOut,
  ChevronUp,
  Plus,
  Pencil,
  Check,
  X,
  Loader2,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useIsMobile } from '@/hooks/use-mobile';
import { useUpdateDisplayName } from '@/hooks/useUserProfile';
import { RoleBadge } from '@/components/ui/RoleBadge';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '@/components/ui/sheet';
import { BusinessSwitcher } from '@/components/business/BusinessSwitcher';
import { cn } from '@/lib/utils';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

interface QuickAction {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  onClick: () => void;
  /** Who can see this action */
  visibility: 'super_admin' | 'firm_owner' | 'business_admin' | 'staff' | 'utility';
  /** Variant for styling */
  variant?: 'default' | 'destructive';
}

export function AccountContextCenter() {
  const [open, setOpen] = useState(false);
  const [switchAnnouncement, setSwitchAnnouncement] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');
  const editInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const triggerRef = useRef<HTMLButtonElement>(null);

  const {
    signOut,
    orgName,
    displayName,
    activeBusiness,
    businessMemberships,
    user,
    isSuperAdmin,
    isOrgAdmin,
    singleBusinessMode,
    activeOrg,
  } = useAuth();

  const updateDisplayName = useUpdateDisplayName();

  // Return focus to trigger and reset edit state when menu closes
  useEffect(() => {
    if (!open) {
      setIsEditingName(false);
      setEditNameValue('');
      if (triggerRef.current) {
        const timer = setTimeout(() => triggerRef.current?.focus(), 50);
        return () => clearTimeout(timer);
      }
    }
  }, [open]);

  // Focus the edit input when editing starts
  useEffect(() => {
    if (isEditingName) {
      editInputRef.current?.focus();
    }
  }, [isEditingName]);

  // Display values
  const displayFirmName = orgName ?? 'LawAssist';
  const userDisplayName = displayName ?? user?.email ?? '';
  const userEmail = user?.email ?? '';
  
  // Determine if we should show business switcher
  // Hide if: super admin, single_business_mode, or ≤1 business in the ACTIVE org.
  // Must filter by active org — raw businessMemberships spans all orgs.
  const orgBusinessCount = activeOrg
    ? businessMemberships.filter(bm => bm.orgId === activeOrg.orgId).length
    : 0;
  const showBusinessSwitcher = !isSuperAdmin && !singleBusinessMode && orgBusinessCount > 1;

  // Navigation handler that closes the menu
  const handleNavigate = (path: string) => {
    navigate(path);
    setOpen(false);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
  };

  const startEditingName = () => {
    setEditNameValue(displayName ?? user?.email ?? '');
    setIsEditingName(true);
  };

  const cancelEditingName = () => {
    setIsEditingName(false);
    setEditNameValue('');
  };

  const saveDisplayName = () => {
    const trimmed = editNameValue.trim();
    if (!trimmed || trimmed === displayName) {
      cancelEditingName();
      return;
    }
    updateDisplayName.mutate(trimmed, {
      onSuccess: () => setIsEditingName(false),
    });
  };

  const handleEditNameKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation(); // Prevent menu keyboard nav from interfering
    if (e.key === 'Enter') {
      e.preventDefault();
      saveDisplayName();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEditingName();
    }
  };

  // Check visibility for quick actions
  // Super Admins ONLY see super_admin + utility items
  // They do NOT see firm/business scoped actions
  const canSeeAction = (visibility: QuickAction['visibility']): boolean => {
    if (isSuperAdmin) {
      return visibility === 'super_admin' || visibility === 'utility';
    }
    
    switch (visibility) {
      case 'utility':
        // Universal items shown to all authenticated users
        return true;
      case 'super_admin':
        return false; // Already handled above
      case 'firm_owner':
        return isOrgAdmin;
      case 'business_admin':
        return isOrgAdmin || activeBusiness?.role === 'firm_owner';
      case 'staff':
        // All business members can see staff-level items
        return true;
      default:
        return false;
    }
  };

  // Define quick actions with role-based visibility
  const quickActions: QuickAction[] = [
    // Super Admin only actions
    {
      label: 'Create Firm',
      icon: Plus,
      onClick: () => handleNavigate('/platform/firms?create=true'),
      visibility: 'super_admin',
    },
  ];

  // Menu items - role-scoped (cleaned up: removed items now in sidebar/hub pages)
  const menuItems: QuickAction[] = [
    {
      label: 'Billing & Plans',
      icon: CreditCard,
      onClick: () => handleNavigate('/settings/billing'),
      visibility: 'firm_owner',
    },
    {
      label: 'Integrations',
      icon: Puzzle,
      onClick: () => handleNavigate('/settings/integrations'),
      visibility: 'business_admin',
    },
  ];

  // Support items - utility visibility (always shown to ALL users including Super Admin)
  const supportItems: QuickAction[] = [
    {
      label: 'Help & Support',
      icon: HelpCircle,
      onClick: () => handleNavigate('/help'),
      visibility: 'utility',
    },
    {
      label: 'Sign Out',
      icon: LogOut,
      onClick: handleSignOut,
      visibility: 'utility',
      variant: 'destructive',
    },
  ];

  // Filter actions based on visibility
  const visibleQuickActions = quickActions.filter(a => canSeeAction(a.visibility));
  const visibleMenuItems = menuItems.filter(a => canSeeAction(a.visibility));
  const visibleSupportItems = supportItems.filter(a => canSeeAction(a.visibility));

  // Keyboard navigation state
  const menuRef = useRef<HTMLDivElement>(null);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  // All focusable items for keyboard navigation
  const allItems = [...visibleQuickActions, ...visibleMenuItems, ...visibleSupportItems];
  const totalItems = allItems.length;

  // Reset focus when menu closes
  useEffect(() => {
    if (!open) {
      setFocusedIndex(-1);
    }
  }, [open]);

  // Focus item by index
  const focusItem = useCallback((index: number) => {
    if (!menuRef.current) return;
    const items = menuRef.current.querySelectorAll<HTMLElement>('button[data-menu-item]');
    if (items.length === 0) return;

    let targetIndex = index;
    if (targetIndex < 0) targetIndex = items.length - 1;
    if (targetIndex >= items.length) targetIndex = 0;

    setFocusedIndex(targetIndex);
    items[targetIndex]?.focus();
  }, []);

  // Keyboard event handler
  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (!menuRef.current) return;
    const items = menuRef.current.querySelectorAll<HTMLElement>('button[data-menu-item]');
    if (items.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        focusItem(focusedIndex + 1);
        break;

      case 'ArrowUp':
        event.preventDefault();
        focusItem(focusedIndex - 1);
        break;

      case 'Home':
        event.preventDefault();
        focusItem(0);
        break;

      case 'End':
        event.preventDefault();
        focusItem(items.length - 1);
        break;

      case 'Escape':
        event.preventDefault();
        setOpen(false);
        break;

      case 'Tab':
        // Allow Tab to close menu and move focus naturally
        setOpen(false);
        break;

      default:
        // Type-ahead: focus item starting with typed character
        if (event.key.length === 1 && /[a-zA-Z]/.test(event.key)) {
          const char = event.key.toLowerCase();
          const startIndex = focusedIndex + 1;
          
          for (let i = 0; i < items.length; i++) {
            const idx = (startIndex + i) % items.length;
            const text = items[idx].textContent?.trim().toLowerCase() ?? '';
            if (text.startsWith(char)) {
              event.preventDefault();
              focusItem(idx);
              break;
            }
          }
        }
        break;
    }
  }, [focusedIndex, focusItem]);

  // Render the trigger button with smooth transitions and accessibility
  const TriggerButton = (
    <button 
      ref={triggerRef}
      aria-haspopup="menu"
      aria-expanded={open}
      aria-label={`Account menu for ${displayFirmName}`}
      className="flex items-center gap-3 w-full hover:bg-sidebar-accent rounded-lg p-2 transition-all duration-200 ease-out group min-h-[56px] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
    >
      <div className="h-10 w-10 rounded-full bg-sidebar-primary/10 flex items-center justify-center group-hover:bg-sidebar-primary/20 group-hover:scale-105 transition-all duration-200 ease-out">
        <Building className="h-5 w-5 text-sidebar-primary transition-transform duration-200 group-hover:scale-110" />
      </div>
      <div className="flex-1 min-w-0 text-left">
        <p className="text-sm font-medium text-sidebar-foreground truncate transition-colors duration-200 group-hover:text-sidebar-primary">
          {displayFirmName}
        </p>
        <p className="text-xs text-sidebar-foreground/70 truncate">
          {userDisplayName}
        </p>
      </div>
      <ChevronUp className={cn(
        "h-4 w-4 text-sidebar-foreground/50 transition-all duration-200 ease-out",
        open ? "opacity-100 rotate-0" : "opacity-0 group-hover:opacity-100 rotate-180"
      )} />
    </button>
  );

  // Screen reader announcement for business switches
  const LiveRegion = (
    <div 
      role="status" 
      aria-live="polite" 
      aria-atomic="true"
      className="sr-only"
    >
      {switchAnnouncement}
    </div>
  );

  // Render the content (shared between Popover and Sheet) with keyboard navigation
  const ContextContent = (
    <div 
      ref={menuRef}
      className="animate-fade-in" 
      role="menu"
      aria-label="Account menu"
      onKeyDown={handleKeyDown}
    >
      {/* Firm Header with Role Badges */}
      <div className="p-4 border-b">
        <div className="flex items-start gap-3">
          <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 transition-transform duration-200 hover:scale-105">
            <Building className="h-6 w-6 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-sm truncate">{displayFirmName}</h3>
            {isEditingName ? (
              <div className="flex items-center gap-1 mt-1">
                <Input
                  ref={editInputRef}
                  value={editNameValue}
                  onChange={(e) => setEditNameValue(e.target.value)}
                  onKeyDown={handleEditNameKeyDown}
                  className="h-7 text-xs px-2"
                  maxLength={100}
                  disabled={updateDisplayName.isPending}
                />
                <button
                  onClick={saveDisplayName}
                  disabled={updateDisplayName.isPending}
                  className="p-1 rounded hover:bg-accent text-primary shrink-0"
                  aria-label="Save display name"
                >
                  {updateDisplayName.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Check className="h-3.5 w-3.5" />
                  )}
                </button>
                <button
                  onClick={cancelEditingName}
                  disabled={updateDisplayName.isPending}
                  className="p-1 rounded hover:bg-accent text-muted-foreground shrink-0"
                  aria-label="Cancel editing"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-1 group/name">
                <p className="text-xs text-foreground truncate">{userDisplayName}</p>
                {!isSuperAdmin && (
                  <button
                    onClick={startEditingName}
                    className={cn(
                      "p-0.5 rounded hover:bg-accent transition-opacity shrink-0",
                      isMobile ? "opacity-100" : "opacity-0 group-hover/name:opacity-100 focus:opacity-100"
                    )}
                    aria-label="Edit display name"
                  >
                    <Pencil className="h-3 w-3 text-muted-foreground" />
                  </button>
                )}
              </div>
            )}
            {!isEditingName && displayName && userEmail && displayName !== userEmail && (
              <p className="text-xs text-muted-foreground truncate">{userEmail}</p>
            )}
            
            {/* Role Badges with subtle entrance animation */}
            <div className="mt-2 flex flex-wrap gap-1.5">
              {/* Platform Badge - Super Admin */}
              {isSuperAdmin && (
                <Badge 
                  variant="default" 
                  className="text-[10px] px-1.5 py-0.5 bg-amber-500 hover:bg-amber-600 text-white transition-all duration-200 hover:scale-105"
                >
                  Super Admin
                </Badge>
              )}
              
              {/* Firm-level Role Badge */}
              {isOrgAdmin && !isSuperAdmin && (
                <Badge
                  variant="default"
                  className="text-[10px] px-1.5 py-0.5 bg-purple-500 hover:bg-purple-600 text-white transition-all duration-200 hover:scale-105"
                >
                  {activeOrg?.role === 'owner' ? 'Firm Owner' : 'Firm Admin'}
                </Badge>
              )}

              {/* Business Role Badge — only for non-org-level users */}
              {activeBusiness && !isOrgAdmin && (
                <RoleBadge role={activeBusiness.role} size="sm" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Business Switcher - hidden in single business mode */}
      {showBusinessSwitcher && (
        <div className="p-3 border-b">
          <p className="text-xs text-muted-foreground mb-2">Active Business</p>
          <BusinessSwitcher showRoleBadge={false} variant="inline" className="w-full" onSwitchComplete={() => setOpen(false)} />
        </div>
      )}

      {/* Quick Actions - role-aware with hover animations and keyboard nav */}
      {visibleQuickActions.length > 0 && (
        <div className="p-2 border-b" role="group" aria-label="Quick Actions">
          <p className="text-xs text-muted-foreground px-3 py-1">Quick Actions</p>
          {visibleQuickActions.map((action, index) => (
            <button
              key={action.label}
              data-menu-item
              role="menuitem"
              tabIndex={-1}
              className="w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md hover:bg-accent focus:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-all duration-150 ease-out text-left group"
              style={{ animationDelay: `${index * 50}ms` }}
              onClick={action.onClick}
            >
              <action.icon className="h-4 w-4 text-primary transition-transform duration-150 group-hover:scale-110 group-focus:scale-110" />
              <span className="transition-colors duration-150 group-hover:text-primary group-focus:text-primary">{action.label}</span>
            </button>
          ))}
        </div>
      )}

      {/* Menu Items with staggered animations and keyboard nav */}
      <div className="p-2" role="group" aria-label="Settings">
        {visibleMenuItems.map((item, index) => (
          <button
            key={item.label}
            data-menu-item
            role="menuitem"
            tabIndex={-1}
            className="w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md hover:bg-accent focus:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-all duration-150 ease-out text-left group"
            style={{ animationDelay: `${(visibleQuickActions.length + index) * 50}ms` }}
            onClick={item.onClick}
          >
            <item.icon className="h-4 w-4 text-muted-foreground transition-all duration-150 group-hover:text-foreground group-hover:scale-110 group-focus:text-foreground group-focus:scale-110" />
            <span className="transition-colors duration-150">{item.label}</span>
          </button>
        ))}
        
        <Separator className="my-2" />
        
        {visibleSupportItems.map((item, index) => (
          <button
            key={item.label}
            data-menu-item
            role="menuitem"
            tabIndex={-1}
            className={cn(
              "w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md transition-all duration-150 ease-out text-left group focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
              item.variant === 'destructive' 
                ? "hover:bg-destructive/10 focus:bg-destructive/10 text-destructive" 
                : "hover:bg-accent focus:bg-accent"
            )}
            style={{ animationDelay: `${(visibleQuickActions.length + visibleMenuItems.length + index) * 50}ms` }}
            onClick={item.onClick}
          >
            <item.icon className={cn(
              "h-4 w-4 transition-all duration-150 group-hover:scale-110 group-focus:scale-110",
              item.variant === 'destructive' && "group-hover:text-destructive group-focus:text-destructive"
            )} />
            <span>{item.label}</span>
          </button>
        ))}
      </div>
    </div>
  );

  // Render mobile (Sheet) or desktop (Popover)
  if (isMobile) {
    return (
      <>
        {LiveRegion}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            {TriggerButton}
          </SheetTrigger>
          <SheetContent side="bottom" className="p-0 max-h-[85vh] overflow-y-auto rounded-t-xl touch-pan-y">
            <VisuallyHidden>
              <SheetTitle>Account Menu</SheetTitle>
            </VisuallyHidden>
            {ContextContent}
          </SheetContent>
        </Sheet>
      </>
    );
  }

  return (
    <>
      {LiveRegion}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          {TriggerButton}
        </PopoverTrigger>
        <PopoverContent 
          side="top" 
          align="start" 
          className="w-72 p-0 mb-2" 
          sideOffset={8}
        >
          {ContextContent}
        </PopoverContent>
      </Popover>
    </>
  );
}
